export const KEY_MAP: Record<string, number> = {
  AltLeft: 18,
  AltRight: 225,
  ArrowDown: 40,
  ArrowLeft: 37,
  ArrowRight: 39,
  ArrowUp: 38,
  Backquote: 192,
  Backslash: 220,
  Backspace: 8,
  BracketLeft: 219,
  BracketRight: 221,
  BrowserHome: 172,
  CapsLock: 20,
  Comma: 188,
  ContextMenu: 93,
  MetaLeft: 17,
  MetaRight: 17,
  OSLeft: 17,
  OSRight: 17,
  ControlLeft: 17,
  ControlRight: 17,
  Delete: 46,
  Digit0: 48,
  Digit1: 49,
  Digit2: 50,
  Digit3: 51,
  Digit4: 52,
  Digit5: 53,
  Digit6: 54,
  Digit7: 55,
  Digit8: 56,
  Digit9: 57,
  End: 35,
  Enter: 13,
  Equal: 187,
  Escape: 27,
  F1: 112,
  F10: 121,
  F11: 122,
  F12: 123,
  F2: 113,
  F3: 114,
  F4: 115,
  F5: 116,
  F6: 117,
  F7: 118,
  F8: 119,
  F9: 120,
  Help: 47,
  Home: 36,
  Insert: 45,
  KeyA: 65,
  KeyB: 66,
  KeyC: 67,
  KeyD: 68,
  KeyE: 69,
  KeyF: 70,
  KeyG: 71,
  KeyH: 72,
  KeyI: 73,
  KeyJ: 74,
  KeyK: 75,
  KeyL: 76,
  KeyM: 77,
  KeyN: 78,
  KeyO: 79,
  KeyP: 80,
  KeyQ: 81,
  KeyR: 82,
  KeyS: 83,
  KeyT: 84,
  KeyU: 85,
  KeyV: 86,
  KeyW: 87,
  KeyX: 88,
  KeyY: 89,
  KeyZ: 90,
  Minus: 189,
  NumLock: 144,
  Numpad0: 96,
  Numpad1: 97,
  Numpad2: 98,
  Numpad3: 99,
  Numpad4: 100,
  Numpad5: 101,
  Numpad6: 102,
  Numpad7: 103,
  Numpad8: 104,
  Numpad9: 105,
  NumpadAdd: 107,
  NumpadDecimal: 108,
  NumpadDivide: 111,
  NumpadEnter: 13,
  NumpadEqual: 187,
  NumpadMultiply: 106,
  NumpadSubtract: 109,
  PageDown: 34,
  PageUp: 33,
  Pause: 19,
  Period: 190,
  Quote: 222,
  ScrollLock: 145,
  Semicolon: 186,
  ShiftLeft: 16,
  ShiftRight: 16,
  Slash: 191,
  Space: 32,
  Tab: 9,
};

export const MODIFIER_KEYS = [
  'AltLeft',
  'AltRight',
  'CapsLock',
  'ShiftLeft',
  'ShiftRight',
  'MetaLeft',
  'MetaRight',
  'OSLeft',
  'OSRight',
  'ControlLeft',
  'ControlRight',
];

export const calculateNaturalValue = (
  scaledValue: number,
  scaledMeasure: number,
  naturalMeasure: number,
): number => {
  if (scaledMeasure === 0) return 0;
  const naturalValue = (scaledValue * naturalMeasure) / scaledMeasure;
  return Math.round(naturalValue);
};

/**
 * Calculate the rendered video bounds within a canvas element using object-fit: contain.
 * Returns the offset and size of the actual video content within the element.
 */
export const getVideoRenderBounds = (
  elementWidth: number,
  elementHeight: number,
  videoWidth: number,
  videoHeight: number,
): {
  offsetX: number;
  offsetY: number;
  renderWidth: number;
  renderHeight: number;
} => {
  if (
    elementWidth === 0 ||
    elementHeight === 0 ||
    videoWidth === 0 ||
    videoHeight === 0
  ) {
    return { offsetX: 0, offsetY: 0, renderWidth: 0, renderHeight: 0 };
  }

  const elementAspect = elementWidth / elementHeight;
  const videoAspect = videoWidth / videoHeight;

  let renderWidth: number;
  let renderHeight: number;

  if (videoAspect > elementAspect) {
    // Video is wider than element (relative to height) - letterboxed (bars on top/bottom)
    renderWidth = elementWidth;
    renderHeight = elementWidth / videoAspect;
  } else {
    // Video is taller than element (relative to width) - pillarboxed (bars on left/right)
    renderHeight = elementHeight;
    renderWidth = elementHeight * videoAspect;
  }

  // Content is centered within the element
  const offsetX = (elementWidth - renderWidth) / 2;
  const offsetY = (elementHeight - renderHeight) / 2;

  return { offsetX, offsetY, renderWidth, renderHeight };
};

/**
 * Map mouse coordinates from element space to video content space,
 * accounting for object-fit: contain letterboxing/pillarboxing.
 * Returns null if the coordinates are outside the video bounds.
 */
export const mapToVideoCoordinates = (
  mouseX: number,
  mouseY: number,
  elementWidth: number,
  elementHeight: number,
  videoWidth: number,
  videoHeight: number,
): { x: number; y: number } | null => {
  const bounds = getVideoRenderBounds(
    elementWidth,
    elementHeight,
    videoWidth,
    videoHeight,
  );

  // Calculate position relative to the video render area
  const relativeX = mouseX - bounds.offsetX;
  const relativeY = mouseY - bounds.offsetY;

  // Check if the mouse is outside the video bounds
  if (
    relativeX < 0 ||
    relativeY < 0 ||
    relativeX > bounds.renderWidth ||
    relativeY > bounds.renderHeight
  ) {
    return null;
  }

  // Map to video coordinates
  const videoX = (relativeX / bounds.renderWidth) * videoWidth;
  const videoY = (relativeY / bounds.renderHeight) * videoHeight;

  return {
    x: Math.round(videoX),
    y: Math.round(videoY),
  };
};
